/**
 * Hooks for evidence number management
 */

import { useState, useEffect, useCallback } from 'react'
import type {
  EvidenceNumber,
  EvidenceNumberFilter,
  EvidenceStatusLogEntry,
  EvidenceSummary,
  PaginatedEvidenceNumbers,
  RegisterEvidenceNumberRequest,
} from '../types/evidence-numbers'
import {
  listEvidenceNumbers,
  getEvidenceNumber as fetchEvidenceNumber,
  getEvidenceEndpointIds,
  getEvidenceSummary,
  getStatusHistory,
  listAmendments,
  registerEvidenceNumber,
  registerBatch,
} from '../services/evidence-numbers'

// =============================================================================
// useEvidenceNumbers - List with filtering and pagination
// =============================================================================

interface UseEvidenceNumbersResult {
  data: EvidenceNumber[]
  total: number
  page: number
  perPage: number
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useEvidenceNumbers(
  caseId: string | null,
  filter?: EvidenceNumberFilter
): UseEvidenceNumbersResult {
  const [result, setResult] = useState<PaginatedEvidenceNumbers>({
    data: [],
    total: 0,
    page: 1,
    perPage: 50,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Serialise the filter — same rationale as useAnalysisArtefacts/useAnalysisFacets.
  //
  // `filter` is an object, and callers pass an object literal, so its identity changes on every
  // render. With `filter` itself as a dep, `fetch` was rebuilt each render, the effect refired,
  // `setResult` re-rendered, and round it went — an unbounded refetch storm that locked the tab
  // (ERR_INSUFFICIENT_RESOURCES) whenever the Chain of Custody section was opened. Depending on
  // the serialised value instead means the effect refires only when the filter's *contents*
  // change.
  const filterKey = JSON.stringify(filter ?? {})

  const fetch = useCallback(async () => {
    if (!caseId) return
    setLoading(true)
    setError(null)
    try {
      // Pass `filter` through (not a reconstructed literal); `filterKey` is the dep sentinel.
      const data = await listEvidenceNumbers(caseId, filter)
      setResult(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch evidence numbers')
    } finally {
      setLoading(false)
    }
    // Intentionally omits `filter` (unstable object reference); depends on `filterKey` instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, filterKey])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { ...result, loading, error, refetch: fetch }
}

// =============================================================================
// useEvidenceNumber - Single evidence number detail
// =============================================================================

interface UseEvidenceNumberResult {
  evidenceNumber: EvidenceNumber | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useEvidenceNumber(
  caseId: string | null,
  id: string | null
): UseEvidenceNumberResult {
  const [evidenceNumber, setEvidenceNumber] = useState<EvidenceNumber | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId || !id) {
      setEvidenceNumber(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const data = await fetchEvidenceNumber(caseId, id)
      setEvidenceNumber(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch evidence number')
    } finally {
      setLoading(false)
    }
  }, [caseId, id])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { evidenceNumber, loading, error, refetch: fetch }
}

// =============================================================================
// useEvidenceSummary - Dashboard summary counts
// =============================================================================

interface UseEvidenceSummaryResult {
  summary: EvidenceSummary | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useEvidenceSummary(caseId: string | null): UseEvidenceSummaryResult {
  const [summary, setSummary] = useState<EvidenceSummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId) {
      setSummary(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const data = await getEvidenceSummary(caseId)
      setSummary(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch evidence summary')
    } finally {
      setLoading(false)
    }
  }, [caseId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { summary, loading, error, refetch: fetch }
}

// =============================================================================
// useEvidenceStatusHistory - Status change log
// =============================================================================

interface UseEvidenceStatusHistoryResult {
  entries: EvidenceStatusLogEntry[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useEvidenceStatusHistory(
  caseId: string | null,
  evidenceNumberId: string | null
): UseEvidenceStatusHistoryResult {
  const [entries, setEntries] = useState<EvidenceStatusLogEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId || !evidenceNumberId) {
      setEntries([])
      return
    }
    setLoading(true)
    setError(null)
    try {
      const data = await getStatusHistory(caseId, evidenceNumberId)
      setEntries(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch status history')
    } finally {
      setLoading(false)
    }
  }, [caseId, evidenceNumberId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { entries, loading, error, refetch: fetch }
}

// =============================================================================
// useRegisterEvidence - Mutation hook
// =============================================================================

interface UseRegisterEvidenceResult {
  registering: boolean
  error: string | null
  register: (req: RegisterEvidenceNumberRequest) => Promise<EvidenceNumber | null>
  registerBatchEndpoints: (endpointIds: string[]) => Promise<EvidenceNumber[] | null>
  clearError: () => void
}

export function useRegisterEvidence(caseId: string): UseRegisterEvidenceResult {
  const [registering, setRegistering] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const registerSingle = useCallback(
    async (req: RegisterEvidenceNumberRequest): Promise<EvidenceNumber | null> => {
      setRegistering(true)
      setError(null)
      try {
        return await registerEvidenceNumber(caseId, req)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to register evidence number')
        return null
      } finally {
        setRegistering(false)
      }
    },
    [caseId]
  )

  const registerBatchEndpoints = useCallback(
    async (endpointIds: string[]): Promise<EvidenceNumber[] | null> => {
      setRegistering(true)
      setError(null)
      try {
        return await registerBatch(caseId, { endpointIds })
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to register evidence numbers')
        return null
      } finally {
        setRegistering(false)
      }
    },
    [caseId]
  )

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return {
    registering,
    error,
    register: registerSingle,
    registerBatchEndpoints,
    clearError,
  }
}

// =============================================================================
// useEvidenceAmendments - List amendments for a base number
// =============================================================================

interface UseEvidenceAmendmentsResult {
  amendments: EvidenceNumber[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useEvidenceAmendments(
  caseId: string | null,
  baseEvidenceNumberId: string | null
): UseEvidenceAmendmentsResult {
  const [amendments, setAmendments] = useState<EvidenceNumber[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId || !baseEvidenceNumberId) {
      setAmendments([])
      return
    }
    setLoading(true)
    setError(null)
    try {
      const data = await listAmendments(caseId, baseEvidenceNumberId)
      setAmendments(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch amendments')
    } finally {
      setLoading(false)
    }
  }, [caseId, baseEvidenceNumberId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { amendments, loading, error, refetch: fetch }
}

// =============================================================================
// useEvidenceEndpointIds - endpoints that already hold an evidence number
// =============================================================================

interface UseEvidenceEndpointIdsResult {
  endpointIds: string[]
  /** True when the backend capped the projection — the set below is incomplete. */
  truncated: boolean
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * IDs of endpoints in the case that already hold a base evidence number.
 *
 * Replaces a second `useEvidenceNumbers(caseId, { numberType: 'base', perPage: 1000 })` instance
 * in the Register view. That instance was wrong twice over: the backend clamps `perPage` to 100,
 * so on a case with more than 100 evidence numbers the derived "unregistered endpoints" set was
 * silently incorrect; and it fetched full evidence-number rows purely to read one id from each.
 */
export function useEvidenceEndpointIds(caseId: string | null): UseEvidenceEndpointIdsResult {
  const [endpointIds, setEndpointIds] = useState<string[]>([])
  const [truncated, setTruncated] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId) {
      setEndpointIds([])
      setTruncated(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const data = await getEvidenceEndpointIds(caseId)
      setEndpointIds(data.endpointIds)
      setTruncated(data.truncated)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch registered endpoints')
    } finally {
      setLoading(false)
    }
  }, [caseId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { endpointIds, truncated, loading, error, refetch: fetch }
}
